import * as React from 'react'
import { createContext, useCallback, useContext, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { X, CheckCircle2, AlertTriangle, Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'

type ToastVariant = 'success' | 'error' | 'info'

type ToastItem = {
  id: string
  variant: ToastVariant
  title: string
  description?: string
}

type ToastContextValue = {
  toasts: ToastItem[]
  toast: (t: Omit<ToastItem, 'id'>) => string
  dismiss: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within ToastProvider')
  return ctx
}

export function ToastProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [toasts, setToasts] = useState<ToastItem[]>([])

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const toast = useCallback(
    (t: Omit<ToastItem, 'id'>): string => {
      const id = crypto.randomUUID()
      setToasts((prev) => [...prev, { ...t, id }])
      const ms = t.variant === 'error' ? 6000 : 4000
      window.setTimeout(() => dismiss(id), ms)
      return id
    },
    [dismiss],
  )

  const value = useMemo(() => ({ toasts, toast, dismiss }), [toasts, toast, dismiss])

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastViewport toasts={toasts} onDismiss={dismiss} />
    </ToastContext.Provider>
  )
}

function ToastViewport({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[]
  onDismiss: (id: string) => void
}): React.ReactElement | null {
  const root = document.body
  return createPortal(
    <div
      aria-live="polite"
      aria-atomic="false"
      aria-label="Notifications"
      className="pointer-events-none fixed inset-x-4 bottom-4 z-50 flex flex-col gap-2 md:inset-x-auto md:right-4 md:max-w-sm md:w-full"
    >
      {toasts.map((t) => (
        <ToastCard key={t.id} item={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>,
    root,
  )
}

function variantStyles(variant: ToastVariant): string {
  switch (variant) {
    case 'success':
      return 'border-enterprise-success/20 bg-enterprise-surface shadow-none'
    case 'error':
      return 'border-enterprise-danger/20 bg-enterprise-surface shadow-none'
    case 'info':
    default:
      return 'border-enterprise-border bg-enterprise-surface shadow-none'
  }
}

function IconFor({ variant }: { variant: ToastVariant }): React.ReactElement {
  if (variant === 'success') return <CheckCircle2 className="h-4 w-4 text-enterprise-success" aria-hidden="true" />
  if (variant === 'error') return <AlertTriangle className="h-4 w-4 text-enterprise-danger" aria-hidden="true" />
  return <Info className="h-4 w-4 text-enterprise-muted" aria-hidden="true" />
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }): React.ReactElement {
  // auto-focus close button for screen reader? no, keep polite.
  const { t } = useTranslation()
  return (
    <div
      role="status"
      className={cn(
        'pointer-events-auto flex items-start gap-3 rounded-none border p-3 text-sm motion-safe:transition-all motion-safe:duration-base motion-reduce:transition-none',
        variantStyles(item.variant),
      )}
    >
      <span className="mt-0.5 shrink-0">
        <IconFor variant={item.variant} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="font-medium leading-5 text-enterprise-text">{item.title}</p>
        {item.description ? (
          <p className="mt-0.5 break-words text-xs leading-4 text-enterprise-muted">{item.description}</p>
        ) : null}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label={t('common.dismiss')}
        className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-transparent bg-transparent text-enterprise-muted hover:bg-enterprise-raised hover:text-enterprise-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-enterprise-indigo motion-reduce:transition-none"
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </div>
  )
}
